import i18next from 'i18next';
import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { cashBookingRefusalKey } from '../domain/cashBookingRefusal';

function formatQueryParam(value: unknown): string {
    if (value instanceof Date) {
        return value.toISOString();
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return value as string;
}

function extractErrorDetails(errorBody: Record<string, unknown>): string[] {
    const parts: string[] = [];

    if (typeof errorBody.message === 'string') {
        parts.push(errorBody.message);
    }

    if (Array.isArray(errorBody.details)) {
        const msgs = errorBody.details
            .map((d: { msg?: string; path?: string }) => (d.path ? `${d.path}: ${d.msg}` : d.msg))
            .filter(Boolean);
        if (msgs.length > 0) {
            parts.push(msgs.join('; '));
        }
    }

    if (Array.isArray(errorBody.missingFields)) {
        parts.push(errorBody.missingFields.join(', '));
    }

    return parts;
}

/**
 * The translatable key of an error body that carries no `error` text, e.g. the e-invoice API's
 * `{ code, messageKey }` or a validation answer's `{ errors: [{ field, messageKey }] }`.
 */
function extractMessageKey(errorBody: Record<string, unknown>): string | null {
    if (typeof errorBody.messageKey === 'string') {
        return errorBody.messageKey;
    }

    if (Array.isArray(errorBody.errors)) {
        const keyed = errorBody.errors.find(
            (entry: { messageKey?: unknown }) => typeof entry?.messageKey === 'string',
        );
        return keyed?.messageKey ?? null;
    }

    return null;
}

async function parseErrorMessage(result: Response): Promise<string> {
    const fallback = `Response status code: ${result.status} ${result.statusText}`;
    try {
        const errorBody = await result.json();
        console.error('API Error Response:', errorBody);
        // A refused cash booking is shown in the operator's language, not the backend's English.
        const refusalKey = cashBookingRefusalKey(errorBody);
        if (refusalKey) {
            return i18next.t(refusalKey);
        }
        if (!errorBody.error) {
            return extractMessageKey(errorBody) ?? fallback;
        }
        const details = extractErrorDetails(errorBody);
        return details.length > 0
            ? `${errorBody.error} - ${details.join(' - ')}`
            : String(errorBody.error);
    } catch {
        return fallback;
    }
}

/**
 * Shared API request configuration and logic used by useQuery and useRequest hooks
 */
export function useApiRequest() {
    const { token, API_BASE_URL } = useAuth();

    const appendQueryParam = useCallback(
        (queryParams: URLSearchParams, key: string, value: unknown) => {
            if (value === undefined || value === null) return;
            queryParams.append(key, formatQueryParam(value));
        },
        [],
    );

    const buildQueryString = useCallback(
        (params: Record<string, unknown>): string => {
            const queryParams = new URLSearchParams();
            for (const [key, value] of Object.entries(params)) {
                if (value === undefined || value === null) continue;
                if (Array.isArray(value)) {
                    value.forEach(v => appendQueryParam(queryParams, key, v));
                } else {
                    appendQueryParam(queryParams, key, value);
                }
            }
            return queryParams.toString();
        },
        [appendQueryParam],
    );

    const makeRequest = useCallback(
        async <R>({
            path,
            method = 'GET',
            params,
        }: {
            path: string;
            method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
            params?: Record<string, unknown>;
        }): Promise<R> => {
            let url = `${API_BASE_URL}${path}`;
            let body: BodyInit | undefined;

            if (method === 'GET' && params) {
                const queryString = buildQueryString(params);
                if (queryString) url += `?${queryString}`;
            } else if (params) {
                body = JSON.stringify(params);
            }

            const result = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body,
            });

            // Any 2xx is success. This used to accept only 200 and 201, so the audit endpoint's
            // correct `202 Accepted` - the backend had already stored the row - was reported on
            // 2026-09-16 as "Document scan outcome not recorded". The backend's contract is right;
            // the layer that reads it was too narrow.
            if (!result.ok) {
                const errorMessage = await parseErrorMessage(result);
                throw new Error(errorMessage);
            }

            // A bodiless success (204, or an empty body) has nothing to parse and is not a failure.
            const text = result.status === 204 ? '' : await result.text();
            return (text.length === 0 ? undefined : JSON.parse(text)) as R;
        },
        [token, API_BASE_URL, buildQueryString],
    );

    /**
     * Fetches a binary resource the caller is authorised to see, as a Blob.
     *
     * Needed because an authenticated image cannot be rendered by pointing `<img src>` at it:
     * the browser sends no `Authorization` header on that request. Client portraits used to be
     * served by an unauthenticated static mount, which is exactly why they could be. They are
     * now fetched here and handed to the page as an object URL.
     *
     * `cache: 'no-store'` matches the header the endpoint sets: a portrait is personal data and
     * must not be written to the browser's HTTP cache.
     */
    const makeBlobRequest = useCallback(
        async (
            path: string,
            options?: { method: 'POST'; params: Record<string, unknown> },
        ): Promise<Blob> => {
            const requestOptions: RequestInit = {
                method: options?.method ?? 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                    ...(options ? { 'Content-Type': 'application/json' } : {}),
                },
                cache: 'no-store',
            };
            if (options) {
                requestOptions.body = JSON.stringify(options.params);
            }
            const result = await fetch(`${API_BASE_URL}${path}`, requestOptions);

            if (result.status !== 200) {
                // Deliberately not parsed for a message: the caller renders a placeholder, and
                // an error body for a missing portrait says nothing an operator can act on.
                throw new Error(`Response status code: ${result.status} ${result.statusText}`);
            }

            return await result.blob();
        },
        [token, API_BASE_URL],
    );

    return { makeRequest, makeBlobRequest, token, API_BASE_URL };
}
